import type { Locale, Timezone } from 'next-intl'

import { formatPhoneNumberIntl } from 'react-phone-number-input'

import { isToday, isValid, isYesterday, parseISO } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { ar } from 'date-fns/locale/ar'
import { enUS } from 'date-fns/locale/en-US'

import { defaultLocale } from '@/lib/i18n/i18n-config'

/**
 * Formats a phone number into international grouping, e.g. "+20 100 762 2347".
 * Accepts E.164 with or without the leading "+"; falls back to the raw value
 * when it can't be parsed (e.g. local numbers without a country code).
 */
export const formatPhoneIntl = (
  phone: null | number | string | undefined
): string => {
  if (phone == null) return ''
  const trimmed = String(phone).trim()
  if (!trimmed) return ''
  const e164 = trimmed.startsWith('+') ? trimmed : `+${trimmed}`
  return formatPhoneNumberIntl(e164) || trimmed
}

type FormatDateOptions = {
  dateFormat?: string
  disableRelativeFormatting?: boolean
  fallback?: string
  formatTime?: string
  locale?: Locale
  timezone?: Timezone
}

/**
 * Normalizes a date/time string to ISO 8601 format
 * Handles time-only strings (HH:mm:ss or HH:mm) by converting to full ISO date
 * @param value - Date string in various formats
 * @returns ISO 8601 date string or null
 */
export const normalizeToISO = (
  value: null | string | undefined
): null | string => {
  if (!value) return null

  // Already ISO format (contains 'T' or full date format)
  if (value.includes('T') || value.match(/^\d{4}-\d{2}-\d{2}/)) {
    return value
  }

  // Time-only format (HH:mm:ss or HH:mm)
  const timeOnlyMatch = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (timeOnlyMatch) {
    return `2000-01-01T${value}`
  }

  // Return as-is for other formats and let parseISO handle validation
  return value
}

export const formatDate = (
  value: null | string | undefined,
  {
    dateFormat = 'dd/MM/yyyy',
    disableRelativeFormatting = false,
    fallback = '-',
    formatTime = 'hh:mm a',
    locale,
    timezone
  }: FormatDateOptions = {}
) => {
  const fallbackObj = {
    date: fallback,
    dateTime: fallback,
    time: fallback
  }

  // Normalize the value to ISO format
  const normalizedValue = normalizeToISO(value)

  if (!normalizedValue || !isValid(parseISO(normalizedValue))) {
    return fallbackObj
  }

  const clientLocale =
    locale ??
    (typeof document !== 'undefined' ? document.documentElement.lang : null) ??
    defaultLocale

  const dateFnsLocale = clientLocale === 'ar' ? ar : enUS
  const Yesterday = clientLocale === 'ar' ? 'أمس' : 'Yesterday'
  const Today = clientLocale === 'ar' ? 'اليوم' : 'Today'

  const rawDate = parseISO(normalizedValue)

  const clientTimeZone =
    timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone

  const date = disableRelativeFormatting
    ? formatInTimeZone(rawDate, clientTimeZone, dateFormat, {
        locale: dateFnsLocale
      })
    : isToday(rawDate)
      ? Today
      : isYesterday(rawDate)
        ? Yesterday
        : formatInTimeZone(rawDate, clientTimeZone, dateFormat, {
            locale: dateFnsLocale
          })

  const time = formatInTimeZone(rawDate, clientTimeZone, formatTime, {
    locale: dateFnsLocale
  })

  const dateTime = `${date} ${time}`

  return {
    date,
    dateTime,
    time
  }
}
